import { InferGetStaticPropsType } from 'next';
import { BADGE_TYPES, LOCALE_DICT, TOP_FRAMES_PATH } from '@constants';
import { Layout, LicensingDataProps, LozengeProps, TopPDP } from '@components';
import { getProduct } from '@services/shopify';
import { getStars } from '@services/yotpo';
import { getGlobalProps, GlobalPropsReturn } from '@utils/get-global-props';
import { withLocalizedPaths } from '@utils/locales';
import { getMultipleCollections } from '@services/shopify/operations/get-multiple-collections';
import { BadgeTypes, NormalizedCollection, NormalizedProduct } from '@ts/product';

const { LIMITED, NEW, BACK_IN_STOCK } = BADGE_TYPES;

const Top = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
	return <TopPDP seoMetadata={{ title: `${props.product.name} Top Frame | Pair Eyewear` }} {...props} />;
};

export const getStaticPaths = withLocalizedPaths(async () => {
	return {
		paths: [],
		fallback: 'blocking',
	};
});

export async function getStaticProps({ params, locale }) {
	try {
		const { collection: collectionHandle, handle: productHandle } = params;
		const { countryCode: country, languageCode: language } = LOCALE_DICT[locale];
		if (collectionHandle === 'top-frames') {
			return { notFound: true };
		}

		const initialData = await Promise.allSettled([
			getGlobalProps({ locale }),
			getProduct(productHandle as string, { country, language }),
			getMultipleCollections(collectionHandle as string, { country, language }),
		]);

		const [globalProps, topFrameProduct, currentCollection] = (initialData as PromiseFulfilledResult<unknown>[]).map(
			result => result.value
		) as [GlobalPropsReturn, NormalizedProduct, NormalizedCollection, NormalizedCollection];

		//-- Initial Data Variables --//
		let activeCollection;
		const baseFrameVariantsAvailable = new Set([]),
			frameSwatches = {},
			isLimited = topFrameProduct.tags?.includes(LIMITED.name),
			isNew = topFrameProduct.tags?.includes(NEW.name),
			isBackInStock = topFrameProduct.tags?.includes(BACK_IN_STOCK.name);

		//-- Current Collection === "undefined" --//
		if (!currentCollection) {
			const parentCollection = topFrameProduct.collections?.find(
				({ metafield }) => metafield && metafield.value === topFrameProduct.type
			);
			activeCollection = await getMultipleCollections(parentCollection?.handle, {
				skipVariants: true,
				withProducts: false,
				country,
				language,
			});
		} else activeCollection = currentCollection;

		//-- Main Product Availability --//
		for (let i = 0; i < topFrameProduct.variants.length; i++) {
			const variant = topFrameProduct.variants[i];
			// const isQuantityAvailable = !!variant?.quantityAvailable ? variant.quantityAvailable >= 5 : false;
			// const isInStock = !!variant?.currentlyNotInStock;
			if (!variant.availableForSale) {
				continue;
			}
			baseFrameVariantsAvailable.add(variant.option);
		}

		//-- Swatch & Avilability --//
		const swatchProducts = activeCollection?.products.filter(({ metafields }) => metafields.productSwatch?.url);
		for (const prod of swatchProducts) {
			for (const variant of prod.variants) {
				// Availability
				if (!variant.availableForSale) continue;

				// Swatches
				if (!frameSwatches[variant.option]) {
					frameSwatches[variant.option] = {
						[prod.metafields.productSwatch.url]: { name: prod.name, handle: prod.handle },
					};
				} else {
					frameSwatches[variant.option][prod.metafields.productSwatch.url] = {
						name: prod.name,
						handle: prod.handle,
					};
				}
			}
		}

		//-- Lozenge Metadata + Product Tags --//
		const lozengeColor = activeCollection.metafields?.lozengeColor ?? 'black';
		const licensingDescription = activeCollection.metafields?.licensingDescription ?? null;
		const lozengeEmoji = activeCollection.metafields?.lozengeEmoji ?? null;
		const lozengeImage = activeCollection.metafields?.lozengeImage?.url ?? null;
		const licensingLogo = activeCollection.metafields?.licensingLogo ?? null;
		const licensingTagline = activeCollection.metafields?.licensingTagline ?? null;
		const lozengeTextColor = activeCollection.metafields?.lozengeTextColor ?? 'white';
		const productTags: LozengeProps[] = [
			{
				alignment: 'left',
				shape: 'pill',
				color: lozengeTextColor,
				backgroundColor: lozengeColor,
				url: `${TOP_FRAMES_PATH}/${collectionHandle}`,
				text: activeCollection?.title,
				emoji: lozengeEmoji,
				image: lozengeImage,
			},
		];

		const getProductTag = (productTag: BadgeTypes): LozengeProps => {
			return {
				alignment: 'left',
				shape: 'square',
				color: productTag.color,
				backgroundColor: productTag.backgroundColor,
				text: productTag.displayText,
			};
		};

		if (isNew) {
			productTags.push(getProductTag(NEW));
		}

		if (isBackInStock) {
			productTags.push(getProductTag(BACK_IN_STOCK));
		}

		if (isLimited) {
			productTags.push(getProductTag(LIMITED));
		}
		//-- Licensing Metadata --//
		const licensingImage = topFrameProduct.metafields?.licensingImage ?? null;
		const licensingImageCaption = topFrameProduct.metafields?.licensingImageCaption ?? null;
		const licensingProductDescription = topFrameProduct.metafields?.licensingProductDescription ?? null;
		const productLicensingLogo = topFrameProduct.metafields?.licensingLogo ?? null;
		const productVideo = topFrameProduct.metafields?.productVideo ?? null;
		const licensingData: LicensingDataProps = {
			collectionLicenseDescription: licensingDescription,
			collectionLicenseLogo: licensingLogo,
			collectionLicenseTagline: licensingTagline,
			licensingImage: licensingImage,
			licensingImageCaption: licensingImageCaption,
			licensingProductDescription: licensingProductDescription,
			productLicensingLogo: productLicensingLogo,
		};

		return {
			props: {
				...globalProps,
				frameSwatches,
				licensingData,
				baseFrameVariantsAvailable: Array.from(baseFrameVariantsAvailable),
				page: 'product',
				product: { ...topFrameProduct, variants: [] },
				productTags: productTags ?? [],
				video: productVideo,
				yotpo: await getStars(topFrameProduct.id),
			},
			revalidate: 600,
		};
	} catch (error) {
		console.error(`SSG error in /top-frames/${params?.collection}/${params?.handle}: ${error}`);

		return {
			notFound: true,
			revalidate: 600,
		} as const;
	}
}

Top.getLayout = function getLayout(page, pageProps) {
	return <Layout pageProps={pageProps}>{page}</Layout>;
};

export default Top;
